// Firebase init for the danvantri project — Auth (Google) + Firestore.
// Per-user PHR data lives at users/{uid}/** (enforced by firestore.rules).
// Storage is intentionally NOT wired (no DICOM/images yet; bucket is deny-all).
//
// HIPAA: Firestore encrypts data at rest (AES-256, Google-managed keys) and in
// transit (TLS) by default; under the signed Google Cloud BAA that satisfies the
// encryption safeguard. Access control is the per-user uid rule + Google sign-in.
import { initializeApp } from 'firebase/app';
import {
  getAuth, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult,
  setPersistence, indexedDBLocalPersistence, browserLocalPersistence,
  signOut, onAuthStateChanged, type User,
} from 'firebase/auth';
import { initializeFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { writable } from 'svelte/store';
import { isMobile } from './device';

export const firebaseConfig = {
  apiKey: 'AIzaSyBMZTCNDQ_q-Vu_GerG8RmnZ2OC9KHWYF4',
  authDomain: 'health.dreamgiga.ai',   // same-origin auth handler → fixes iOS Safari ITP + desktop popup COOP
  projectId: 'danvantri',
  storageBucket: 'danvantri.firebasestorage.app',
  messagingSenderId: '1049536826524',
  appId: '1:1049536826524:web:8f0b582731fb18eb357159',
};

export const fbApp = initializeApp(firebaseConfig);
export const fbAuth = getAuth(fbApp);
// ignoreUndefinedProperties: the PHR model has many optional fields that may be
// undefined; Firestore would otherwise reject the whole document.
// experimentalAutoDetectLongPolling: Firestore's default WebChannel streaming can
// hang indefinitely on iOS Safari (and behind some corporate proxies / VPNs) — the
// very first read never resolves, wedging the app on "Loading…". Auto-detect probes
// the connection and transparently falls back to long-polling so reads complete.
export const fbDb = initializeFirestore(fbApp, {
  ignoreUndefinedProperties: true,
  experimentalAutoDetectLongPolling: true,
});
export const fbStorage = getStorage(fbApp);   // archives uploaded import files

const provider = new GoogleAuthProvider();
provider.setCustomParameters({ prompt: 'select_account' });

// Keep the session across reloads (IndexedDB → localStorage fallback). Important
// on iOS Safari so a reload doesn't drop the user.
setPersistence(fbAuth, indexedDBLocalPersistence)
  .catch(() => setPersistence(fbAuth, browserLocalPersistence).catch(() => {}));

// Reactive current user — components subscribe to gate UI / trigger sign-in.
export const authUser = writable<User | null>(fbAuth.currentUser);
onAuthStateChanged(fbAuth, (u) => authUser.set(u));

// Complete a pending redirect sign-in (mobile path). Swallow errors so a failed
// resolution never crashes the app on load.
getRedirectResult(fbAuth).catch(() => {});

// Popups are unreliable on iOS Safari (blocked / flaky) — use a full-page
// redirect on mobile, keep the nicer popup on desktop.
export const signInWithGoogle = () =>
  isMobile() ? signInWithRedirect(fbAuth, provider) : signInWithPopup(fbAuth, provider);
export const signOutUser = () => signOut(fbAuth);
export const currentUid = (): string | null => fbAuth.currentUser?.uid ?? null;
